// DEKNINGSANALYSEN — «hvor kan noen gå forbi uoppdaget?»
//
// ══ DET ENE SPØRSMÅLET SKJERMEN FINNES FOR ═════════════════════════════════
// Dette er analysen et jegerlag trenger FØR de legger ut, og etterpå for å svare
// på det ene spørsmålet som betyr noe: hvilke deler av aksen er IKKE dekket?
// `dekningshull()` i kontrakten regner det ut; her presenteres svaret slik at
// forbeholdene ikke kan mistes på veien.
//
// ══ TRE TING SKJERMEN ALDRI GJØR ═══════════════════════════════════════════
//
//  1. DEN VISER ALDRI DEKNING SOM STØRRE ENN DEN ER. Sektorer dekker bare
//     sektoren sin, og en brytertråd dekker ~1,5 m på tvers — ikke en disk. Å
//     tegne dem som disker ville lukket hull som er åpne, og det er nettopp den
//     grafiske løgnen som får et lag til å tro at en akse er dekket.
//  2. DEN SKJULER ALDRI EN UTELATT SENSOR. Hver utelatelse står med sin ÅRSAK
//     ordrett fra kontrakten. En sensor som stilltiende forsvinner fra analysen
//     er en analyse ingen kan etterprøve — og de to vanligste årsakene («hentet
//     inn» og «lenke tapt») er begge ting jegeren må handle på.
//  3. DEN PÅSTÅR ALDRI AT «DEKKET» ER SIKKERT. Terreng er ikke modellert.
//     «Ikke dekket» er sikkert; «dekket» forutsetter at deteksjonsradien holder i
//     dette terrenget, og en rygg, en myr eller tett gran forkorter den kraftig.
//     Kontraktens advarsel om dette gjengis ordrett, hver gang.
//
// ══ HVORFOR `krevSambandslenke` ER EN BRYTER OG IKKE EN ANTAKELSE ══════════
// Det er TO ulike spørsmål, og begge er reelle for et jegerlag:
//   * «Hvor kan noen gå forbi uten å bli REGISTRERT?» — da teller en loggende
//     sensor uten lenke, for utløsningen finnes i noden.
//   * «Hvor kan noen gå forbi uten at vi får VARSEL i tid?» — da teller den ikke.
// Valget er jegerens, og svaret kan være helt forskjellig. Derfor står bryteren
// på skjermen med begge spørsmålene skrevet ut, ikke bare et hakekryss.
import {
  BRYTERTRAD_DEKNING_M, antattEksponeringsrisiko, dekningshull, latLonToMgrs,
  type DekningsAnalyse, type UgsSensor,
} from '@skade/contracts';
import {
  bryter, div, felt, h, knapp, merke, nv, p, sett, span, tomt,
} from '@skade/sensorflate';
import { LENKE_NAVN, POSISJONSMETODE_NAVN, SKJULT_NAVN, EKSPONERING_NAVN } from '../lib/format.js';
import { kjenteSensorer } from '../lib/lager.js';
import { RUTEKILDE_NAVN, SENSORART_NAVN, type Rute } from '../api/typer.js';
import { ART_IKON, ikon } from '../ui/ikoner.js';
import type { Kontekst } from './kontekst.js';

/**
 * Skjermens egen tilstand. Den ligger i modulen og ikke i lageret fordi det er
 * en VISNINGSINNSTILLING, ikke data: hvilken rute jegeren ser på, og hvilket av
 * de to spørsmålene han stiller. Å legge den i lageret ville gjort en
 * bro-oppdatering til noe som kunne endre hva han ser på.
 */
let valgtRute: string | null = null;
let krevLenke = false;

export interface Dekningssammendrag {
  ruter: number;
  storsteHullM: number | null;
}

export function tegnDekning(vert: HTMLElement, ctx: Kontekst): Dekningssammendrag {
  const t = ctx.tilstand();
  const ruter = ctx.ruter();
  const sensorer = kjenteSensorer(t);

  if (ruter.length === 0) {
    sett(
      vert,
      tomt(
        'Ingen akse å analysere langs',
        'Dekningsanalysen svarer på «hvor kan noen gå forbi uoppdaget» LANGS en rute. Ingen rute er meldt inn ennå. '
        + 'Ruta kommer fra broen (planen), eller fra en spor-logg laget selv har gått. Kartlaget der man kan tegne en akse '
        + 'for hånd er IKKE bygget ennå — derfor står denne tom, og ikke med en oppdiktet akse.',
        { ikon: ikon('hull') },
      ),
    );
    return { ruter: 0, storsteHullM: null };
  }

  const rute = ruter.find((r) => r.id === valgtRute) ?? ruter[0];
  valgtRute = rute.id;

  const rot = div('dek');
  rot.append(tegnRutevalg(ruter, rute, ctx));
  rot.append(tegnBryter(ctx));

  const svar = dekningshull(sensorer, rute.punkt, { krevSambandslenke: krevLenke });
  if (!svar.ok) {
    // Kontraktens egen årsak, ORDRETT. Den sier presis hva som er galt med ruta.
    rot.append(feilbolk('Analysen kunne ikke kjøres', svar.arsak));
    sett(vert, rot);
    return { ruter: ruter.length, storsteHullM: null };
  }

  const a = svar.analyse;
  rot.append(tegnSvar(a, rute));
  rot.append(tegnHull(a));
  rot.append(tegnSperrepunkter(a));
  rot.append(tegnSensorutvalg(a, sensorer));
  rot.append(tegnForbehold(a));
  sett(vert, rot);
  return { ruter: ruter.length, storsteHullM: a.storsteHullM };
}

// ── Valg ────────────────────────────────────────────────────────────────────

function tegnRutevalg(ruter: readonly Rute[], valgt: Rute, ctx: Kontekst): HTMLElement {
  const rot = div('dek-bolk');
  rot.append(h(3, 'dek-bolkhode', 'Akse'));
  const rad = div('dek-rutevalg');
  for (const r of ruter) {
    const b = knapp(r.navn, () => {
      valgtRute = r.id;
      ctx.kunngjør(`Analyserer langs ${r.navn}`);
      ctx.tegnPåNytt();
    }, { variant: r.id === valgt.id ? 'primær' : 'stille' });
    b.setAttribute('aria-pressed', r.id === valgt.id ? 'true' : 'false');
    rad.append(b);
  }
  rot.append(rad);
  // Rutas KILDE avgjør hvor mye analysen er verdt: hullkantene i en antatt akse
  // kan ligge titalls meter feil før en eneste sensor er regnet inn.
  rot.append(nv('Kilde', RUTEKILDE_NAVN[valgt.kilde], valgt.kilde === 'antatt' ? 'advarsel' : ''));
  rot.append(nv('Knekkpunkt', String(valgt.punkt.length)));
  if (valgt.merknad) rot.append(p('dek-brød', valgt.merknad));
  if (valgt.ovelse) rot.append(merke('ØVING', 'ovelse'));
  return rot;
}

function tegnBryter(ctx: Kontekst): HTMLElement {
  const rot = div('dek-bolk');
  rot.append(h(3, 'dek-bolkhode', 'Hvilket spørsmål stiller du?'));
  rot.append(p(
    'dek-brød',
    krevLenke
      ? 'NÅ: «Hvor kan noen gå forbi uten at vi får VARSEL i tid?» Sensorer uten sambandslenke teller IKKE som dekning.'
      : 'NÅ: «Hvor kan noen gå forbi uten å bli REGISTRERT?» En loggende sensor uten lenke teller som dekning — utløsningen finnes i noden, men du får den først når noden hentes.',
  ));
  rot.append(bryter('Krev sambandslenke (varsling i tid)', krevLenke, (på) => {
    krevLenke = på;
    ctx.kunngjør(på
      ? 'Krever nå sambandslenke: analysen viser hvor VARSLING mangler'
      : 'Krever ikke sambandslenke: analysen viser hvor REGISTRERING mangler');
    ctx.tegnPåNytt();
  }));
  return rot;
}

// ── Svaret ──────────────────────────────────────────────────────────────────

function tegnSvar(a: DekningsAnalyse, rute: Rute): HTMLElement {
  const rot = div('dek-bolk dek-svar');
  rot.append(h(3, 'dek-bolkhode', 'Svar'));

  // Det STØRSTE hullet er svaret. Dekningsandelen er et forførende tall — 90 %
  // dekning med ett hull på 200 m midt i aksen er en åpen akse.
  const stort = div('dek-storste');
  stort.append(ikon(a.storsteHullM > 0 ? 'hull' : 'sjekk'));
  stort.append(span('dek-storstetall', a.hull.length === 0
    ? 'Ingen hull funnet i denne oppløsningen'
    : `Største hull: ${Math.round(a.storsteHullM)} m`));
  rot.append(stort);

  const tall = div('dek-tallrad');
  tall.append(felt('Aksens lengde', `${Math.round(a.ruteLengdeM)} m`));
  tall.append(felt('Dekket', `${Math.round(a.dekketM)} m (${Math.round(a.dekningsandel * 100)} %)`));
  tall.append(felt('Antall hull', String(a.hull.length)));
  tall.append(felt('Oppløsning', `${a.opplosningM.toFixed(2).replace('.', ',')} m`));
  rot.append(tall);
  rot.append(p(
    'dek-brød',
    `Alle hullkanter er KVANTISERT til ${a.opplosningM.toFixed(2).replace('.', ',')} m — et hull oppgitt som `
    + `${Math.round(a.storsteHullM)} m er ${Math.round(a.storsteHullM)} m ±${a.opplosningM.toFixed(1).replace('.', ',')} m. `
    + `Aksen «${rute.navn}» er analysert i ${Math.round(a.ruteLengdeM / a.opplosningM)} celler.`,
  ));
  return rot;
}

function tegnHull(a: DekningsAnalyse): HTMLElement {
  const rot = div('dek-bolk');
  rot.append(h(3, 'dek-bolkhode', `Hull (${a.hull.length})`));
  if (a.hull.length === 0) {
    rot.append(p('dek-brød', 'Ingen udekkede strekk i denne oppløsningen — men les forbeholdet om terreng nederst.'));
    return rot;
  }
  // Sortert på LENGDE, ikke på posisjon: det største hullet er det man tetter
  // først, og det skal ikke ligge midt i en liste sortert på meter fra start.
  const sortert = [...a.hull].sort((x, y) => y.lengdeM - x.lengdeM);
  for (const hull of sortert) {
    const rad = div('dek-hull');
    const hode = div('dek-hullhode');
    hode.append(ikon('hull'));
    hode.append(span('dek-hullengde', `${Math.round(hull.lengdeM)} m udekket`));
    hode.append(div('dek-rom'));
    hode.append(span('dek-hullsted', `${Math.round(hull.fraM)}–${Math.round(hull.tilM)} m fra aksens start`));
    rad.append(hode);
    // Midtpunktet er stedet man ville krysset for å ha størst margin — altså
    // stedet motparten velger. Grid, ikke desimalgrader: det er dette man leser
    // opp og det er dette man setter av i kartet.
    rad.append(nv('Midt i hullet (MGRS)', latLonToMgrs(hull.midtPunkt), 'grid'));
    rad.append(nv('Fra', `${hull.fraPunkt.lat.toFixed(5)}, ${hull.fraPunkt.lon.toFixed(5)}`));
    rad.append(nv('Til', `${hull.tilPunkt.lat.toFixed(5)}, ${hull.tilPunkt.lon.toFixed(5)}`));
    rot.append(rad);
  }
  return rot;
}

function tegnSperrepunkter(a: DekningsAnalyse): HTMLElement {
  const rot = div('dek-bolk');
  rot.append(h(3, 'dek-bolkhode', `Sperrepunkter — brytertråd (${a.sperrepunkter.length})`));
  if (a.sperrepunkter.length === 0) {
    rot.append(p('dek-brød', 'Ingen brytertråd krysser denne aksen.'));
    return rot;
  }
  rot.append(p(
    'dek-brød',
    `En brytertråd lukker bare ~${BRYTERTRAD_DEKNING_M} m på tvers. Den som FØLGER aksen kan ikke passere den uoppdaget — `
    + 'men hullene på hver side er fullt reelle for den som går utenom. Sperrepunktet er derfor en LÅS på ruta, ikke dekning av den.',
  ));
  for (const sp of a.sperrepunkter) {
    const rad = div('dek-sperre');
    const hode = div('dek-hullhode');
    hode.append(ikon('sperre'));
    hode.append(span('dek-hullengde', sp.sensorId));
    hode.append(div('dek-rom'));
    hode.append(span('dek-hullsted', `${Math.round(sp.langsM)} m fra aksens start`));
    rad.append(hode);
    rad.append(nv('Krysning (MGRS)', latLonToMgrs(sp.punkt), 'grid'));
    // Kvantiseringen står, fordi kontrakten sier at verdien er midtpunktet i
    // FØRSTE cellen tråden dekker — den kan ligge én celle før det geometriske
    // krysningspunktet.
    rad.append(p('dek-note', `Kvantisert til ${a.opplosningM.toFixed(2).replace('.', ',')} m — kan ligge én celle før det geometriske krysningspunktet.`));
    rot.append(rad);
  }
  return rot;
}

// ── Sensorutvalget ──────────────────────────────────────────────────────────

/**
 * Hvem som ble regnet med, og hvem som IKKE ble — med årsak.
 *
 * De utelatte står FØRST. Det er motsatt av hva som er naturlig, og med vilje: en
 * hentet node og en node med tapt lenke er de to tilstandene som gjør en analyse
 * feil på den farlige måten, altså ved å vise MER dekning enn det finnes.
 */
function tegnSensorutvalg(a: DekningsAnalyse, sensorer: readonly UgsSensor[]): HTMLElement {
  const kart = new Map(sensorer.map((s) => [s.sensorId, s]));
  const rot = div('dek-bolk');
  rot.append(h(3, 'dek-bolkhode', `Sensorutvalg: ${a.vurderteSensorer.length} med, ${a.utelatteSensorer.length} utelatt`));

  if (a.utelatteSensorer.length > 0) {
    const ute = div('dek-utelatt');
    ute.append(h(3, 'dek-underhode', 'UTELATT fra analysen'));
    for (const u of a.utelatteSensorer) {
      const s = kart.get(u.sensorId);
      const rad = div('dek-sensorrad dek-sensorrad-ute');
      const hode = div('dek-hullhode');
      if (s) hode.append(ikon(ART_IKON[s.art]));
      hode.append(span('dek-sensornavn', s?.navn ?? u.sensorId));
      if (s) hode.append(merke(SENSORART_NAVN[s.art], `art-${s.art}`));
      rad.append(hode);
      // Årsaken ORDRETT fra kontrakten.
      rad.append(p('dek-utelattgrunn', `Årsak: ${u.arsak}`));
      rot.append(rad);
    }
    rot.append(ute);
  }

  for (const id of a.vurderteSensorer) {
    const s = kart.get(id);
    if (!s) continue;
    const rad = div('dek-sensorrad');
    const hode = div('dek-hullhode');
    hode.append(ikon(ART_IKON[s.art]));
    hode.append(span('dek-sensornavn', s.navn));
    hode.append(merke(SENSORART_NAVN[s.art], `art-${s.art}`));
    hode.append(div('dek-rom'));
    hode.append(span('dek-hullsted', geometritekst(s)));
    rad.append(hode);
    rad.append(nv('Posisjon', `${POSISJONSMETODE_NAVN[s.posisjonMetode]}`, s.posisjonMetode === 'anslått' ? 'advarsel' : ''));
    rad.append(nv('Lenke', LENKE_NAVN[s.lenke.kvalitet], s.lenke.kvalitet === 'god' ? '' : 'advarsel'));
    // Skjulthet og eksponering hører HIT og ikke bare i sensorstolpen: en
    // OPPDAGET sensor er verre enn ingen sensor, og dette er skjermen der jegeren
    // planlegger hvor nodene skal ligge.
    rad.append(nv('Skjult', SKJULT_NAVN[s.skjultGrad], s.skjultGrad === 'synlig' ? 'advarsel' : ''));
    const eksp = antattEksponeringsrisiko(s);
    rad.append(nv(
      'Eksponering',
      `${EKSPONERING_NAVN[eksp]}${s.eksponeringsrisiko ? '' : ' (ANTATT av heuristikk — din egen vurdering på stedet vinner)'}`,
      eksp === 'høy' ? 'advarsel' : '',
    ));
    rot.append(rad);
  }
  return rot;
}

function geometritekst(s: UgsSensor): string {
  const g = s.geometri;
  if (g.form === 'linje') return `tråd ${g.lengdeM} m i akse ${Math.round(g.retningDeg)}°`;
  if (g.form === 'sektor') {
    return `sektor ${g.radiusM} m, ${Math.round(g.retningDeg)}° ±${Math.round(g.apningsvinkelDeg / 2)}°`
      + `${g.retningUsikkerhetDeg > 0 ? ` (retning ±${Math.round(g.retningUsikkerhetDeg)}° 1σ)` : ''}`;
  }
  return `rundstrålende ${g.radiusM} m`;
}

// ── Forbeholdene ────────────────────────────────────────────────────────────

/**
 * Kontraktens `advarsler` og `begrunnelse`, ORDRETT.
 *
 * Advarslene står ALLTID åpne. Terrengforbeholdet er den viktigste setningen på
 * hele skjermen — uten det leses «84 % dekket» som om aksen er stengt — og en
 * advarsel bak et klikk er en advarsel som ikke er gitt.
 */
function tegnForbehold(a: DekningsAnalyse): HTMLElement {
  const rot = div('dek-bolk');
  rot.append(h(3, 'dek-bolkhode', 'Forbehold'));
  for (const adv of a.advarsler) rot.append(p('dek-advarsel', adv));

  const det = document.createElement('details');
  det.className = 'dek-utledning';
  const sum = document.createElement('summary');
  sum.textContent = `Utledning fra kontrakten (${a.begrunnelse.length} linjer) — kan etterregnes`;
  det.append(sum);
  const liste = div('dek-utledningslinjer');
  for (const b of a.begrunnelse) liste.append(p('dek-utledningslinje', b));
  det.append(liste);
  rot.append(det);
  return rot;
}

function feilbolk(tittel: string, melding: string): HTMLElement {
  const rot = div('dek-bolk dek-feil');
  rot.setAttribute('role', 'alert');
  const hode = div('dek-hullhode');
  hode.append(ikon('varsel'));
  hode.append(h(3, 'dek-bolkhode', tittel));
  rot.append(hode);
  rot.append(p('dek-brød', melding));
  return rot;
}
